import NetworkManager from './NetworkManager';

export interface User {
  yumId: string;
  firstName: string;
  lastName: string;
  email: string;
  roles: string[];
  permissions: string[];
  userType?: 'internal' | 'vendor';
  authMethod?: 'mapp' | 'email';
  onboardingCompleted?: boolean;
  // Vendor-specific fields (present when userType === 'vendor')
  vendorId?: string;
  vendorRole?: 'admin' | 'user';
  vendorName?: string;
}

export interface PermissionsResponse {
  status: boolean;
  data: User;
  accessToken: string;
}

// Email/OTP Authentication Types
export interface RequestOTPResponse {
  status: boolean;
  message: string;
  expiresInMinutes: number;
}

export interface VerifyOTPResponse {
  status: boolean;
  data: User;
  accessToken: string;
  isFirstLogin: boolean;
}

export interface OnboardingPreferences {
  timezone?: string;
  notificationPreferences?: {
    email?: boolean;
  };
}

export interface CompleteOnboardingResponse {
  status: boolean;
  message: string;
}

export interface CurrentUserResponse {
  status: boolean;
  data: User;
}

export interface Store {
  internalStoreNumber: string;
  champsNumber: string;
  storeName: string;
  brand: string;
  country: string;
  address?: {
    street?: string;
    city?: string;
  };
  timezone?: string;
  isTestStore: boolean;
  latitude?: number;
  longitude?: number;
  // Organizational hierarchy
  aisOrganizationId?: string;
  territoryId?: string;
  regionId?: string;
  regionName?: string;
  marketId?: string;
  areaId?: string;
  aisStoreId?: string;
  businessUnitId?: string;
}

export interface StoresResponse {
  status: boolean;
  data: Store[];
  count: number;
}

export async function redeemMappToken(mappToken: string): Promise<PermissionsResponse> {
  return NetworkManager.post<PermissionsResponse>('/permissions', { mappToken });
}

export async function getUserStores(): Promise<StoresResponse> {
  return NetworkManager.get<StoresResponse>('/stores?detailed=true');
}

// ===== Brands API =====

export interface BrandsResponse {
  status: boolean;
  data: {
    brands: string[];
    primaryBrand: string | null;
  };
}

/**
 * Get user's available brands for brand selection
 */
export async function getUserBrands(): Promise<BrandsResponse> {
  return NetworkManager.get<BrandsResponse>('/brands');
}

// ===== Email/OTP Authentication API =====

/**
 * Request OTP code for email authentication
 * @param email - User's email address
 */
export async function requestOTP(email: string): Promise<RequestOTPResponse> {
  return NetworkManager.post<RequestOTPResponse>('/auth/request-otp', { email });
}

/**
 * Verify OTP code and receive JWT token
 * @param email - User's email address
 * @param code - OTP code received via email
 */
export async function verifyOTP(email: string, code: string): Promise<VerifyOTPResponse> {
  return NetworkManager.post<VerifyOTPResponse>('/auth/verify-otp', { email, code });
}

/**
 * Complete onboarding for first-time users
 * @param preferences - User's onboarding preferences
 */
export async function completeOnboarding(
  preferences: OnboardingPreferences
): Promise<CompleteOnboardingResponse> {
  return NetworkManager.post<CompleteOnboardingResponse>('/auth/complete-onboarding', preferences);
}

/**
 * Get current user info (works for both auth methods)
 */
export async function getCurrentUser(): Promise<CurrentUserResponse> {
  return NetworkManager.get<CurrentUserResponse>('/auth/me');
}

// ===== Admin API (Vendor Management) =====

export type VendorUserRole = 'admin' | 'user';

// Vendor Organization
export interface Vendor {
  vendorId: string;
  name: string;
  contactEmail: string;
  description?: string;
  isActive: boolean;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  // Supplier fields (for QC Complaints)
  isSupplier?: boolean;
  supplierCategories?: string[];
  supplierBrands?: string[];
}

export interface VendorUser {
  email: string;
  userId: string;
  firstName: string;
  lastName?: string;
  vendorId: string;
  vendorName?: string;
  vendorRole: VendorUserRole;
  userType: 'vendor';
  invitedBy?: string;
  createdAt: number;
  updatedAt: number;
  isActive: boolean;
  lastLoginAt?: number;
  onboardingCompleted: boolean;
}

export interface DomainWhitelist {
  domain: string;
  vendorId: string;
  vendorName?: string;
  defaultVendorRole: VendorUserRole;
  addedBy: string;
  createdAt: number;
  isActive: boolean;
}

export interface Invitation {
  email: string;
  invitationId: string;
  vendorId: string;
  vendorName?: string;
  vendorRole: VendorUserRole;
  invitedBy: string;
  invitedByName: string;
  createdAt: number;
  expiresAt: number;
  status: 'pending' | 'accepted' | 'expired';
}

export interface VendorListResponse {
  status: boolean;
  data: Vendor[];
}

export interface VendorUserListResponse {
  status: boolean;
  data: VendorUser[];
  count?: number;
  lastKey?: string;
}

export interface DomainWhitelistListResponse {
  status: boolean;
  data: DomainWhitelist[];
}

export interface InvitationListResponse {
  status: boolean;
  data: Invitation[];
}

export interface AdminActionResponse {
  status: boolean;
  message: string;
}

export interface CreateVendorRequest {
  name: string;
  contactEmail: string;
  description?: string;
}

export interface UpdateVendorRequest {
  name?: string;
  contactEmail?: string;
  description?: string;
  isActive?: boolean;
}

export interface AddDomainWhitelistRequest {
  domain: string;
  vendorId: string;
  defaultVendorRole?: VendorUserRole;
}

export interface InviteVendorRequest {
  email: string;
  vendorId: string;
  vendorRole?: VendorUserRole;
}

export interface UpdateVendorUserAccessRequest {
  vendorRole?: VendorUserRole;
}

// ===== Vendor Organization APIs =====

export async function createVendor(data: CreateVendorRequest): Promise<{ status: boolean; data: Vendor; message: string }> {
  return NetworkManager.post<{ status: boolean; data: Vendor; message: string }>('/vendors', data);
}

export async function getVendorOrganizations(activeOnly = true): Promise<VendorListResponse> {
  return NetworkManager.get<VendorListResponse>(`/vendors?activeOnly=${activeOnly}`);
}

export async function getVendorOrganization(vendorId: string): Promise<{ status: boolean; data: Vendor }> {
  return NetworkManager.get<{ status: boolean; data: Vendor }>(`/vendors/${encodeURIComponent(vendorId)}`);
}

export async function updateVendorOrganization(
  vendorId: string,
  data: UpdateVendorRequest
): Promise<{ status: boolean; data: Vendor; message: string }> {
  return NetworkManager.patch<{ status: boolean; data: Vendor; message: string }>(
    `/vendors/${encodeURIComponent(vendorId)}`,
    data as unknown as Record<string, unknown>
  );
}

export async function deactivateVendorOrganization(vendorId: string): Promise<AdminActionResponse> {
  return NetworkManager.delete<AdminActionResponse>(`/vendors/${encodeURIComponent(vendorId)}`);
}

export async function getVendorOrganizationUsers(vendorId: string): Promise<VendorUserListResponse> {
  return NetworkManager.get<VendorUserListResponse>(`/vendors/${encodeURIComponent(vendorId)}/users`);
}

// Supplier Status APIs
export interface MarkAsSupplierRequest {
  supplierCategories?: string[];
  supplierBrands?: string[];
}

export async function markVendorAsSupplier(
  vendorId: string,
  data?: MarkAsSupplierRequest
): Promise<{ status: boolean; data: Vendor; message: string }> {
  return NetworkManager.post<{ status: boolean; data: Vendor; message: string }>(
    `/vendors/${encodeURIComponent(vendorId)}/supplier`,
    data || {}
  );
}

export async function removeVendorSupplierStatus(
  vendorId: string
): Promise<{ status: boolean; data: Vendor; message: string }> {
  return NetworkManager.delete<{ status: boolean; data: Vendor; message: string }>(
    `/vendors/${encodeURIComponent(vendorId)}/supplier`
  );
}

export async function updateVendorSupplierDetails(
  vendorId: string,
  data: MarkAsSupplierRequest
): Promise<{ status: boolean; data: Vendor; message: string }> {
  return NetworkManager.patch<{ status: boolean; data: Vendor; message: string }>(
    `/vendors/${encodeURIComponent(vendorId)}/supplier`,
    data as unknown as Record<string, unknown>
  );
}

// Domain Whitelist APIs
export async function addDomainWhitelist(
  data: AddDomainWhitelistRequest
): Promise<AdminActionResponse> {
  return NetworkManager.post<AdminActionResponse>('/admin/domain-whitelist', data);
}

export async function getDomainWhitelist(vendorId?: string): Promise<DomainWhitelistListResponse> {
  const url = vendorId
    ? `/admin/domain-whitelist?vendorId=${encodeURIComponent(vendorId)}`
    : '/admin/domain-whitelist';
  return NetworkManager.get<DomainWhitelistListResponse>(url);
}

export async function removeDomainWhitelist(domain: string): Promise<AdminActionResponse> {
  return NetworkManager.delete<AdminActionResponse>(`/admin/domain-whitelist/${encodeURIComponent(domain)}`);
}

// Invitation APIs
export async function inviteVendorUser(data: InviteVendorRequest): Promise<AdminActionResponse> {
  return NetworkManager.post<AdminActionResponse>('/admin/invite-vendor', data);
}

export async function getInvitations(vendorId?: string): Promise<InvitationListResponse> {
  const url = vendorId
    ? `/admin/invitations?vendorId=${encodeURIComponent(vendorId)}`
    : '/admin/invitations';
  return NetworkManager.get<InvitationListResponse>(url);
}

export async function resendInvitation(email: string): Promise<AdminActionResponse> {
  return NetworkManager.post<AdminActionResponse>(`/admin/invitations/${encodeURIComponent(email)}/resend`, {});
}

export async function cancelInvitation(email: string): Promise<AdminActionResponse> {
  return NetworkManager.delete<AdminActionResponse>(`/admin/invitations/${encodeURIComponent(email)}`);
}

// Vendor User Management APIs
export async function getVendorUsers(vendorId?: string, lastKey?: string): Promise<VendorUserListResponse> {
  const params = new URLSearchParams();
  if (vendorId) params.append('vendorId', vendorId);
  if (lastKey) params.append('lastKey', lastKey);
  const url = `/admin/vendor-users${params.toString() ? `?${params.toString()}` : ''}`;
  return NetworkManager.get<VendorUserListResponse>(url);
}

export async function getVendorUser(email: string): Promise<{ status: boolean; data: VendorUser }> {
  return NetworkManager.get<{ status: boolean; data: VendorUser }>(`/admin/vendor-users/${encodeURIComponent(email)}`);
}

export async function updateVendorUserAccess(
  email: string,
  data: UpdateVendorUserAccessRequest
): Promise<AdminActionResponse> {
  return NetworkManager.patch<AdminActionResponse>(
    `/admin/vendor-users/${encodeURIComponent(email)}`,
    data as unknown as Record<string, unknown>
  );
}

export async function deactivateVendorUser(email: string): Promise<AdminActionResponse> {
  return NetworkManager.delete<AdminActionResponse>(`/admin/vendor-users/${encodeURIComponent(email)}`);
}

// Legacy aliases for backwards compatibility
export const getVendors = getVendorUsers;
export const inviteVendor = inviteVendorUser;
export const updateVendorAccess = updateVendorUserAccess;
export const deactivateVendor = deactivateVendorUser;

// ===== Vendor Self-Service Admin API =====
// These endpoints are for vendor admins to manage their own organization
// All operations are automatically scoped to the authenticated user's vendorId

export interface VendorOrgResponse {
  status: boolean;
  data: Vendor;
}

/**
 * Get the authenticated vendor admin's organization details
 */
export async function getMyVendorOrganization(): Promise<VendorOrgResponse> {
  return NetworkManager.get<VendorOrgResponse>('/vendor-admin/organization');
}

/**
 * List all users in the vendor admin's organization
 */
export async function getMyVendorUsers(): Promise<VendorUserListResponse> {
  return NetworkManager.get<VendorUserListResponse>('/vendor-admin/users');
}

/**
 * Update a user's role within the vendor organization
 * @param email - User's email address
 * @param vendorRole - New role ('admin' or 'user')
 */
export async function updateMyVendorUserRole(
  email: string,
  vendorRole: VendorUserRole
): Promise<AdminActionResponse> {
  return NetworkManager.patch<AdminActionResponse>(
    `/vendor-admin/users/${encodeURIComponent(email)}/role`,
    { vendorRole } as unknown as Record<string, unknown>
  );
}

/**
 * Reactivate a deactivated user in the vendor organization
 * @param email - User's email address
 */
export async function reactivateMyVendorUser(email: string): Promise<AdminActionResponse> {
  return NetworkManager.post<AdminActionResponse>(
    `/vendor-admin/users/${encodeURIComponent(email)}/activate`,
    {}
  );
}

/**
 * Deactivate a user in the vendor organization (soft delete)
 * @param email - User's email address
 */
export async function deactivateMyVendorUser(email: string): Promise<AdminActionResponse> {
  return NetworkManager.delete<AdminActionResponse>(
    `/vendor-admin/users/${encodeURIComponent(email)}`
  );
}

/**
 * List pending invitations for the vendor organization
 * @param status - Filter by status (optional)
 */
export async function getMyVendorInvitations(status?: string): Promise<InvitationListResponse> {
  const url = status
    ? `/vendor-admin/invitations?status=${encodeURIComponent(status)}`
    : '/vendor-admin/invitations';
  return NetworkManager.get<InvitationListResponse>(url);
}

/**
 * Invite a new user to the vendor organization
 * @param email - Email address to invite
 * @param vendorRole - Role to assign ('admin' or 'user', defaults to 'user')
 */
export async function inviteToMyVendor(
  email: string,
  vendorRole?: VendorUserRole
): Promise<AdminActionResponse> {
  return NetworkManager.post<AdminActionResponse>('/vendor-admin/invitations', {
    email,
    vendorRole,
  });
}

/**
 * Resend an invitation email
 * @param email - Email address of the pending invitation
 */
export async function resendMyVendorInvitation(email: string): Promise<AdminActionResponse> {
  return NetworkManager.post<AdminActionResponse>(
    `/vendor-admin/invitations/${encodeURIComponent(email)}/resend`,
    {}
  );
}

/**
 * Cancel a pending invitation
 * @param email - Email address of the pending invitation
 */
export async function cancelMyVendorInvitation(email: string): Promise<AdminActionResponse> {
  return NetworkManager.delete<AdminActionResponse>(
    `/vendor-admin/invitations/${encodeURIComponent(email)}`
  );
}

/**
 * List whitelisted domains for the vendor organization
 */
export async function getMyVendorDomains(): Promise<DomainWhitelistListResponse> {
  return NetworkManager.get<DomainWhitelistListResponse>('/vendor-admin/domains');
}

/**
 * Add a domain to the vendor's whitelist
 * @param domain - Domain to whitelist (e.g., 'company.com')
 * @param defaultVendorRole - Default role for users from this domain ('admin' or 'user')
 */
export async function addMyVendorDomain(
  domain: string,
  defaultVendorRole?: VendorUserRole
): Promise<AdminActionResponse> {
  return NetworkManager.post<AdminActionResponse>('/vendor-admin/domains', {
    domain,
    defaultVendorRole,
  });
}

/**
 * Remove a domain from the vendor's whitelist
 * @param domain - Domain to remove
 */
export async function removeMyVendorDomain(domain: string): Promise<AdminActionResponse> {
  return NetworkManager.delete<AdminActionResponse>(
    `/vendor-admin/domains/${encodeURIComponent(domain)}`
  );
}

// ===== User Preferences API (Unified Onboarding) =====

export interface UserPreferencesResponse {
  status: boolean;
  data: {
    onboardingCompleted: boolean;
    preferences: Record<string, unknown>;
  } | null;
}

export interface CompleteUnifiedOnboardingResponse {
  status: boolean;
  message: string;
}

/**
 * Get current user's onboarding state and preferences
 */
export async function getUserPreferences(): Promise<UserPreferencesResponse> {
  return NetworkManager.get<UserPreferencesResponse>('/preferences');
}

/**
 * Complete onboarding for any user type (internal or vendor)
 * @param preferences - Optional onboarding preferences
 */
export async function completeUnifiedOnboarding(
  preferences?: OnboardingPreferences
): Promise<CompleteUnifiedOnboardingResponse> {
  return NetworkManager.put<CompleteUnifiedOnboardingResponse>(
    '/preferences/onboarding',
    (preferences || {}) as unknown as Record<string, unknown>
  );
}

// ===== Complaint Submission API =====

/** Category ID from brand catalog (dynamic, e.g. product_quality or product_quality_abc12). */
export type ComplaintCategory = string;

export type ComplaintStatus = 'submitted' | 'under_review' | 'approved' | 'rejected';

export interface Supplier {
  vendorId: string;
  name: string;
  contactEmail: string;
  supplierCategories?: string[];
  supplierBrands?: string[];
}

export interface SuppliersResponse {
  status: boolean;
  data: Supplier[];
  count: number;
}

export interface SubmitComplaintRequest {
  storeId: string;
  storeName: string;
  brandId: string;
  supplierId: string;
  supplierName: string;
  category: ComplaintCategory;
  /** Catalog product ID — server validates and stores snapshot name */
  productId?: string;
  productName: string;
  productCode?: string;
  description: string;
  manufacturerId?: string;
  manufacturer: string;
  deliveryDate: number;
  batchCode: string;
  affectedUnits: number;
  affectedUnitType?: 'BAG' | 'CASE';
  bagPictureUrl?: string;
  casePictureUrl?: string;
  mediaUrls?: string[];
  /** SHA-256 hex per S3 key for duplicate detection. */
  mediaChecksums?: Record<string, string>;
}

export interface SubmitComplaintResponse {
  status: boolean;
  data: {
    complaintId: string;
    status: ComplaintStatus;
    submittedAt: number;
  };
}

export interface PresignedUrlRequest {
  contentType: string;
  fileName?: string;
  complaintId?: string;
}

export interface PresignedUrlResponse {
  status: boolean;
  data: {
    uploadUrl: string;
    mediaKey: string;
    expiresAt: number;
  };
}

/**
 * Get list of active suppliers for complaint submission
 */
export async function getSuppliers(): Promise<SuppliersResponse> {
  return NetworkManager.get<SuppliersResponse>('/suppliers');
}

// ===== Catalog bulk actions (shared by products / categories / manufacturers) =====

export type BulkCatalogAction = 'delete' | 'deactivate' | 'activate';

export interface BulkCatalogSkippedItem {
  id: string;
  name?: string;
  reason: 'in_use' | 'not_found' | 'error';
  complaintCount?: number;
}

export interface BulkCatalogResult {
  action: BulkCatalogAction;
  requested: number;
  processed: string[];
  skipped: BulkCatalogSkippedItem[];
}

export interface BulkCatalogResponse {
  status: boolean;
  data: BulkCatalogResult;
}

// ===== Manufacturer Catalog API =====

export interface ManufacturerItem {
  manufacturerId: string;
  name: string;
  brandId?: string;
  vendorId?: string;
  isActive?: boolean;
  createdAt?: number;
  updatedAt?: number;
}

export interface ManufacturersResponse {
  status: boolean;
  data: ManufacturerItem[];
  count: number;
}

/** Public: Get manufacturers for complaint form (brand + vendor merged) */
export async function getManufacturers(supplierId?: string): Promise<ManufacturersResponse> {
  const params = supplierId ? `?supplierId=${encodeURIComponent(supplierId)}` : '';
  return NetworkManager.get<ManufacturersResponse>(`/manufacturers${params}`);
}

/** Admin: Get brand-level manufacturers */
export async function getAdminManufacturers(activeOnly = true): Promise<ManufacturersResponse> {
  return NetworkManager.get<ManufacturersResponse>(`/admin/manufacturers?activeOnly=${activeOnly}`);
}

/** Admin: Create manufacturer */
export async function createAdminManufacturer(data: { name: string }): Promise<{ status: boolean; data: ManufacturerItem }> {
  return NetworkManager.post(`/admin/manufacturers`, data);
}

/** Admin: Update manufacturer */
export async function updateAdminManufacturer(
  manufacturerId: string,
  data: { name?: string; isActive?: boolean }
): Promise<{ status: boolean; data: ManufacturerItem }> {
  return NetworkManager.patch(`/admin/manufacturers/${manufacturerId}`, data);
}

/** Admin: Deactivate or reactivate manufacturer (reversible; keeps historical complaint links) */
export async function setAdminManufacturerActive(
  manufacturerId: string,
  isActive: boolean
): Promise<{ status: boolean; data: ManufacturerItem }> {
  return updateAdminManufacturer(manufacturerId, { isActive });
}

/**
 * Admin: Permanently delete manufacturer.
 * Server responds 409 when complaints reference it — deactivate instead.
 */
export async function deleteAdminManufacturer(manufacturerId: string): Promise<{ status: boolean }> {
  return NetworkManager.delete(`/admin/manufacturers/${encodeURIComponent(manufacturerId)}`);
}

/** Admin: Bulk delete / deactivate / activate manufacturers */
export async function bulkAdminManufacturers(
  action: BulkCatalogAction,
  ids: string[]
): Promise<BulkCatalogResponse> {
  return NetworkManager.post<BulkCatalogResponse>('/admin/manufacturers/bulk', { action, ids });
}

/** Admin: Export manufacturers as Excel */
export async function exportAdminManufacturers(activeOnly = true): Promise<Blob> {
  const params: Record<string, string> | undefined = activeOnly ? undefined : { activeOnly: 'false' };
  return NetworkManager.getBlob('/admin/manufacturers/export', params);
}

/** Admin: Import manufacturers from Excel */
export async function importAdminManufacturers(
  file: File
): Promise<{
  status: boolean;
  data?: { created: number; skipped: number; total: number; errors?: Array<{ row: number; message: string }> };
}> {
  const formData = new FormData();
  formData.append('file', file);
  return NetworkManager.postFormData('/admin/manufacturers/import', formData);
}

// ===== Product Catalog API =====

export interface Product {
  productId: string;
  name: string;
  /** Distributor / SKU code (optional) */
  productCode?: string;
  brandId: string;
  vendorId?: string;
  category?: string;
  isActive: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface ProductsResponse {
  status: boolean;
  data: Product[];
  count: number;
}

// ===== Categories API =====

export interface Category {
  categoryId: string;
  label: string;
  description?: string;
  brandId: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: number;
  updatedAt: number;
}

export interface CategoriesResponse {
  status: boolean;
  data: Category[];
  count: number;
}

/**
 * Get complaint categories for complaint form
 */
export async function getCategories(): Promise<CategoriesResponse> {
  return NetworkManager.get<CategoriesResponse>('/categories');
}

/**
 * List complaint categories (admin only, includes inactive)
 */
export async function getAdminCategories(activeOnly = true): Promise<CategoriesResponse> {
  const params = activeOnly ? '' : '?activeOnly=false';
  return NetworkManager.get<CategoriesResponse>(`/admin/categories${params}`);
}

/**
 * Export categories as Excel (admin only)
 */
export async function exportAdminCategories(activeOnly = true): Promise<Blob> {
  const params: Record<string, string> | undefined = activeOnly ? undefined : { activeOnly: 'false' };
  return NetworkManager.getBlob('/admin/categories/export', params);
}

/**
 * Bulk import categories from Excel (admin only)
 */
export async function importAdminCategories(
  file: File
): Promise<{
  status: boolean;
  data?: { created: number; skipped: number; total: number; errors?: Array<{ row: number; message: string }> };
}> {
  const formData = new FormData();
  formData.append('file', file);
  return NetworkManager.postFormData('/admin/categories/import', formData);
}

/**
 * Create complaint category (admin only)
 */
export async function createAdminCategory(data: {
  label: string;
  description?: string;
  sortOrder?: number;
}): Promise<{ status: boolean; data: Category }> {
  return NetworkManager.post<{ status: boolean; data: Category }>('/admin/categories', data);
}

/**
 * Update complaint category (admin only)
 */
export async function updateAdminCategory(
  categoryId: string,
  data: { label?: string; description?: string; isActive?: boolean; sortOrder?: number }
): Promise<{ status: boolean; data: Category }> {
  return NetworkManager.patch<{ status: boolean; data: Category }>(
    `/admin/categories/${encodeURIComponent(categoryId)}`,
    data
  );
}

/**
 * Deactivate or reactivate complaint category (admin only; reversible)
 */
export async function setAdminCategoryActive(
  categoryId: string,
  isActive: boolean
): Promise<{ status: boolean; data: Category }> {
  return updateAdminCategory(categoryId, { isActive });
}

/**
 * Permanently delete complaint category (admin only).
 * Server responds 409 when complaints reference it — deactivate instead.
 */
export async function deleteAdminCategory(categoryId: string): Promise<{ status: boolean }> {
  return NetworkManager.delete<{ status: boolean }>(
    `/admin/categories/${encodeURIComponent(categoryId)}`
  );
}

/**
 * Bulk delete / deactivate / activate categories (admin only)
 */
export async function bulkAdminCategories(
  action: BulkCatalogAction,
  ids: string[]
): Promise<BulkCatalogResponse> {
  return NetworkManager.post<BulkCatalogResponse>('/admin/categories/bulk', { action, ids });
}

// ===== Products API =====

/**
 * Get products for complaint form (brand + optional supplier)
 */
export async function getProducts(supplierId?: string): Promise<ProductsResponse> {
  const params = supplierId ? `?supplierId=${encodeURIComponent(supplierId)}` : '';
  return NetworkManager.get<ProductsResponse>(`/products${params}`);
}

/**
 * List brand-level products (admin only)
 */
export async function getAdminProducts(activeOnly = true): Promise<ProductsResponse> {
  const params = activeOnly ? '' : '?activeOnly=false';
  return NetworkManager.get<ProductsResponse>(`/admin/products${params}`);
}

/**
 * Create brand-level product (admin only)
 */
export async function createAdminProduct(data: {
  name: string;
  category?: string;
  productCode?: string;
}): Promise<{ status: boolean; data: Product }> {
  return NetworkManager.post<{ status: boolean; data: Product }>('/admin/products', data);
}

/**
 * Update brand-level product (admin only)
 */
export async function updateAdminProduct(
  productId: string,
  data: { name?: string; category?: string; productCode?: string | null; isActive?: boolean }
): Promise<{ status: boolean; data: Product }> {
  return NetworkManager.patch<{ status: boolean; data: Product }>(
    `/admin/products/${encodeURIComponent(productId)}`,
    data
  );
}

/**
 * Deactivate or reactivate brand-level product (admin only; reversible)
 */
export async function setAdminProductActive(
  productId: string,
  isActive: boolean
): Promise<{ status: boolean; data: Product }> {
  return updateAdminProduct(productId, { isActive });
}

/**
 * Permanently delete brand-level product (admin only).
 * Server responds 409 when complaints reference it — deactivate instead.
 */
export async function deleteAdminProduct(productId: string): Promise<{ status: boolean }> {
  return NetworkManager.delete<{ status: boolean }>(
    `/admin/products/${encodeURIComponent(productId)}`
  );
}

/**
 * Bulk delete / deactivate / activate brand-level products (admin only)
 */
export async function bulkAdminProducts(
  action: BulkCatalogAction,
  ids: string[]
): Promise<BulkCatalogResponse> {
  return NetworkManager.post<BulkCatalogResponse>('/admin/products/bulk', { action, ids });
}

/**
 * Export brand-level products as Excel (admin only)
 */
export async function exportAdminProducts(activeOnly = true): Promise<Blob> {
  const params: Record<string, string> | undefined = activeOnly ? undefined : { activeOnly: 'false' };
  return NetworkManager.getBlob('/admin/products/export', params);
}

/**
 * Bulk import brand-level products from Excel (admin only)
 */
export async function importAdminProducts(
  file: File
): Promise<{
  status: boolean;
  data?: {
    created: number;
    /** Existing un-coded products that received a code from the sheet */
    updated?: number;
    skipped: number;
    total: number;
    errors?: Array<{ row: number; message: string }>;
  };
}> {
  const formData = new FormData();
  formData.append('file', file);
  return NetworkManager.postFormData('/admin/products/import', formData);
}

/**
 * List vendor-specific products (vendor admin only)
 */
export async function getVendorAdminProducts(activeOnly = true): Promise<ProductsResponse> {
  const params = activeOnly ? '' : '?activeOnly=false';
  return NetworkManager.get<ProductsResponse>(`/vendor-admin/products${params}`);
}

/**
 * Create vendor-specific product (vendor admin only)
 */
export async function createVendorAdminProduct(data: {
  name: string;
  category?: string;
  productCode?: string;
}): Promise<{ status: boolean; data: Product }> {
  return NetworkManager.post<{ status: boolean; data: Product }>(
    '/vendor-admin/products',
    data
  );
}

/**
 * Update vendor-specific product (vendor admin only)
 */
export async function updateVendorAdminProduct(
  productId: string,
  data: { name?: string; category?: string; productCode?: string | null; isActive?: boolean }
): Promise<{ status: boolean; data: Product }> {
  return NetworkManager.patch<{ status: boolean; data: Product }>(
    `/vendor-admin/products/${encodeURIComponent(productId)}`,
    data
  );
}

/**
 * Deactivate or reactivate vendor-specific product (vendor admin only; reversible)
 */
export async function setVendorAdminProductActive(
  productId: string,
  isActive: boolean
): Promise<{ status: boolean; data: Product }> {
  return updateVendorAdminProduct(productId, { isActive });
}

/**
 * Permanently delete vendor-specific product (vendor admin only).
 * Server responds 409 when complaints reference it — deactivate instead.
 */
export async function deleteVendorAdminProduct(productId: string): Promise<{ status: boolean }> {
  return NetworkManager.delete<{ status: boolean }>(
    `/vendor-admin/products/${encodeURIComponent(productId)}`
  );
}

/**
 * Bulk delete / deactivate / activate vendor-specific products (vendor admin only)
 */
export async function bulkVendorAdminProducts(
  action: BulkCatalogAction,
  ids: string[]
): Promise<BulkCatalogResponse> {
  return NetworkManager.post<BulkCatalogResponse>('/vendor-admin/products/bulk', { action, ids });
}

/**
 * Get a presigned URL for uploading media to S3
 * @param data - Content type, optional filename, and optional complaint ID
 */
export async function getPresignedUploadUrl(data: PresignedUrlRequest): Promise<PresignedUrlResponse> {
  return NetworkManager.post<PresignedUrlResponse>('/upload/presigned-url', data);
}

/**
 * Submit a new complaint
 * @param data - Complaint details
 */
export async function submitComplaint(data: SubmitComplaintRequest): Promise<SubmitComplaintResponse> {
  return NetworkManager.post<SubmitComplaintResponse>('/complaints', data);
}

// ===== Complaint List & Detail API =====

export interface Complaint {
  complaintId: string;
  referenceNumber: string; // {brandId}-{YY}-{MM}-{increment} e.g., "TBC-24-01-042"
  storeId: string;
  storeName: string;
  /** Champs ID — supplier-facing store identifier (E14.3). Optional for legacy complaints. */
  champsNumber?: string;
  brandId: string;
  supplierId: string;
  supplierName: string;
  status: ComplaintStatus;
  category: ComplaintCategory;
  /** Label at submit time; UI prefers live catalog label when available */
  categoryLabel?: string;
  productId?: string;
  productName: string;
  productCode?: string;
  description: string;
  temperature?: number;
  manufacturerId?: string;
  manufacturer: string;
  deliveryDate: number;
  batchCode: string;
  affectedUnits: number;
  affectedUnitType?: 'BAG' | 'CASE';
  claimValue?: number;
  bagPictureUrl?: string;
  casePictureUrl?: string;
  mediaUrls: string[];
  submittedBy: string;
  submittedByName: string;
  submittedByType: 'internal' | 'vendor';
  submittedAt: number;
  acknowledgedAt?: number;
  resolvedAt?: number;
  createdAt: number;
  updatedAt: number;
}

export interface ComplaintHistoryEvent {
  eventType: 'status_change' | 'comment' | 'media_added' | 'field_edited';
  previousStatus?: ComplaintStatus;
  newStatus?: ComplaintStatus;
  comment?: string;
  mediaUrl?: string;
  mediaUrls?: string[];
  media?: MediaWithUrl[];
  fieldChanges?: Array<{ field: string; oldValue: string; newValue: string }>;
  userId: string;
  userName: string;
  userType: 'internal' | 'vendor' | 'admin';
  timestamp: number;
}

export interface MediaWithUrl {
  key: string;
  downloadUrl: string;
  /** Inferred from S3 key extension; legacy responses may omit. */
  mimeType?: string;
  /** Last segment of the S3 key (e.g. "abc.pdf"). Legacy responses may omit. */
  fileName?: string;
  /** SHA-256 hex of the file bytes. Used for client-side duplicate detection. */
  checksum?: string;
}

export interface ComplaintWithHistory {
  complaint: Complaint & { media?: MediaWithUrl[] };
  history: ComplaintHistoryEvent[];
}

export interface ComplaintsListParams {
  status?: ComplaintStatus | 'all';
  startDate?: number;
  endDate?: number;
  limit?: number;
  cursor?: string;
  includeStats?: boolean;
  search?: string; // reference number prefix search (e.g., "TBC-26-01")
  storeNumbers?: string[]; // filter by restaurant/store IDs (internalStoreNumber)
}

export interface ComplaintsListResponse {
  status: boolean;
  data: Complaint[];
  count: number;
  nextCursor?: string;
  /** Present when includeStats=true in request */
  stats?: ComplaintStats;
}

export interface ComplaintDetailResponse {
  status: boolean;
  data: ComplaintWithHistory;
}

export interface UpdateStatusRequest {
  status: ComplaintStatus;
  comment?: string;
}

export interface AddCommentRequest {
  comment: string;
  mediaUrls?: string[];
  /** SHA-256 hex per S3 key, for duplicate detection. */
  mediaChecksums?: Record<string, string>;
}

/**
 * Complaint statistics for dashboard
 */
export interface ComplaintStats {
  total: number;
  byStatus: Record<ComplaintStatus, number>;
  pending: number; // submitted + under_review
  resolved: number; // approved + rejected
  recentCount: number; // Last 7 days
}

export interface ComplaintStatsResponse {
  status: boolean;
  data: ComplaintStats;
}

// ==========================================
// Analytics Types (E6)
// ==========================================

/**
 * Analytics metrics for dashboard KPIs
 */
export interface AnalyticsMetrics {
  avgResolutionTime: number | null; // milliseconds (null if no resolved complaints)
  avgAcknowledgmentTime: number | null; // milliseconds
  acknowledgmentRate: number; // percentage (0-100)
  resolutionRate: number; // percentage (0-100)
}

/**
 * Complaint breakdown by supplier
 */
export interface SupplierBreakdown {
  supplierId: string;
  supplierName: string;
  total: number;
  byStatus: Record<ComplaintStatus, number>;
  avgResolutionTime: number | null;
}

/**
 * Complaint breakdown by category
 */
export interface CategoryBreakdown {
  category: ComplaintCategory;
  count: number;
  percentage: number;
}

/**
 * Complaint trend data point
 */
export interface ComplaintTrend {
  date: string; // YYYY-MM-DD
  total: number;
  submitted: number;
  resolved: number;
}

/**
 * Full analytics response
 */
export interface AnalyticsData {
  metrics: AnalyticsMetrics;
  bySupplier: SupplierBreakdown[];
  byCategory: CategoryBreakdown[];
  trends: ComplaintTrend[];
  period: { startDate: number; endDate: number };
}

export interface AnalyticsResponse {
  status: boolean;
  data: AnalyticsData;
}

/**
 * Analytics query options
 */
export interface AnalyticsQueryOptions {
  startDate?: number;
  endDate?: number;
  trendDays?: number; // default 30
}

/**
 * Get complaints list (works for both internal and vendor users)
 * @param params - Optional filters (status, date range, pagination)
 */
export async function getComplaints(params?: ComplaintsListParams): Promise<ComplaintsListResponse> {
  const searchParams = new URLSearchParams();
  if (params?.status && params.status !== 'all') searchParams.append('status', params.status);
  if (params?.startDate) searchParams.append('startDate', params.startDate.toString());
  if (params?.endDate) searchParams.append('endDate', params.endDate.toString());
  if (params?.limit) searchParams.append('limit', params.limit.toString());
  if (params?.cursor) searchParams.append('cursor', params.cursor);
  if (params?.search) searchParams.append('search', params.search);
  if (params?.includeStats) searchParams.append('includeStats', 'true');
  if (params?.storeNumbers && params.storeNumbers.length > 0) {
    searchParams.append('storeNumbers', params.storeNumbers.join(','));
  }

  const url = `/complaints${searchParams.toString() ? `?${searchParams}` : ''}`;
  return NetworkManager.get<ComplaintsListResponse>(url);
}

/**
 * Get complaint detail with history
 * @param complaintId - The complaint ID to fetch
 */
export async function getComplaint(complaintId: string): Promise<ComplaintDetailResponse> {
  return NetworkManager.get<ComplaintDetailResponse>(`/complaints/${encodeURIComponent(complaintId)}`);
}

/**
 * Update complaint status (vendor only)
 * @param complaintId - The complaint ID to update
 * @param data - New status and optional comment
 */
export async function updateComplaintStatus(
  complaintId: string,
  data: UpdateStatusRequest
): Promise<{ status: boolean; data: Complaint }> {
  return NetworkManager.patch<{ status: boolean; data: Complaint }>(
    `/complaints/${encodeURIComponent(complaintId)}/status`,
    data as unknown as Record<string, unknown>
  );
}

/**
 * Update claim value on a complaint (vendor/admin only)
 */
export async function updateClaimValue(
  complaintId: string,
  claimValue: number
): Promise<{ status: boolean; data: Complaint }> {
  return NetworkManager.patch<{ status: boolean; data: Complaint }>(
    `/complaints/${encodeURIComponent(complaintId)}/claim-value`,
    { claimValue } as unknown as Record<string, unknown>
  );
}

/**
 * Update editable fields on a complaint (admin only)
 */
export async function updateComplaintFields(
  complaintId: string,
  fields: Record<string, unknown> & { updatedAt: number }
): Promise<{ status: boolean; data: Complaint }> {
  return NetworkManager.patch<{ status: boolean; data: Complaint }>(
    `/complaints/${encodeURIComponent(complaintId)}`,
    fields as unknown as Record<string, unknown>
  );
}

/**
 * Add comment to complaint
 * @param complaintId - The complaint ID to add a comment to
 * @param data - Comment text
 */
export async function addComplaintComment(
  complaintId: string,
  data: AddCommentRequest
): Promise<{ status: boolean; data: ComplaintHistoryEvent }> {
  return NetworkManager.post<{ status: boolean; data: ComplaintHistoryEvent }>(
    `/complaints/${encodeURIComponent(complaintId)}/comments`,
    data
  );
}

/**
 * Get complaint statistics for dashboard
 */
export async function getComplaintStats(): Promise<ComplaintStatsResponse> {
  return NetworkManager.get<ComplaintStatsResponse>('/complaints/stats');
}

export interface ExportComplaintsParams {
  startDate?: number;
  endDate?: number;
  status?: ComplaintStatus | 'all';
  storeNumbers?: string[];
  search?: string;
}

/**
 * Export complaints as Excel (.xlsx) (admin only)
 * Sends all active list filters so the export matches the UI view.
 * Max 90-day window enforced server-side; capped at 10,000 rows.
 */
export async function exportComplaints(params?: ExportComplaintsParams): Promise<Blob> {
  const searchParams = new URLSearchParams();
  if (params?.startDate) searchParams.append('startDate', params.startDate.toString());
  if (params?.endDate) searchParams.append('endDate', params.endDate.toString());
  if (params?.status && params.status !== 'all') searchParams.append('status', params.status);
  if (params?.storeNumbers && params.storeNumbers.length > 0) {
    searchParams.append('storeNumbers', params.storeNumbers.join(','));
  }
  if (params?.search) searchParams.append('search', params.search);

  const url = `/complaints/export${searchParams.toString() ? `?${searchParams}` : ''}`;

  const response = await fetch(`${NetworkManager.getBaseUrl()}${url}`, {
    method: 'GET',
    headers: NetworkManager.getHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to export complaints');
  }

  return response.blob();
}

/**
 * Get analytics for reporting dashboard
 * @param options - Optional filters (date range, trend days)
 */
export async function getAnalytics(options?: AnalyticsQueryOptions): Promise<AnalyticsResponse> {
  const searchParams = new URLSearchParams();
  if (options?.startDate) searchParams.append('startDate', options.startDate.toString());
  if (options?.endDate) searchParams.append('endDate', options.endDate.toString());
  if (options?.trendDays) searchParams.append('trendDays', options.trendDays.toString());

  const url = `/analytics${searchParams.toString() ? `?${searchParams}` : ''}`;
  return NetworkManager.get<AnalyticsResponse>(url);
}

// Export NetworkManager instance as 'api' for convenience
export const api = NetworkManager;

export { NetworkManager };
